<template>
  <div class="contentmod-dashboard">
    <v-container fluid class="px-6 py-4">
      <!-- Welcome Section -->
      <div class="welcome-section mb-6">
        <div class="welcome-content">
          <div class="welcome-text">
            <h1 class="welcome-title">
              <v-icon large class="vibrant-title-icon mr-3">mdi-shield-check</v-icon>
              ContentMod AI Dashboard
              <span class="welcome-subtitle-inline">Intelligent Content Moderation</span>
            </h1>
            <p class="welcome-subtitle">
              Your intelligent content moderation platform powered by AI
            </p>
          </div>
          <div class="welcome-illustration">
            <img 
              src="@/assets/content-mod-logo.svg" 
              alt="Content Moderation"
              width="120"
              height="120"
            />
          </div>
        </div>
      </div>

      <!-- Quick Stats Grid -->
      <v-row class="mb-6">
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card" elevation="4" hover>
            <v-card-text class="stats-content">
              <div class="stats-icon-wrapper orange--bg">
                <v-icon size="28" color="white">mdi-clock-outline</v-icon>
              </div>
              <div class="stats-text">
                <div class="stats-value">{{ stats.pending_moderation || 0 }}</div>
                <div class="stats-label">Pending Reviews</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card" elevation="4" hover>
            <v-card-text class="stats-content">
              <div class="stats-icon-wrapper green--bg">
                <v-icon size="28" color="white">mdi-check-circle</v-icon>
              </div>
              <div class="stats-text">
                <div class="stats-value">{{ stats.approved_posts || 0 }}</div>
                <div class="stats-label">Approved Posts</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card" elevation="4" hover>
            <v-card-text class="stats-content">
              <div class="stats-icon-wrapper red--bg">
                <v-icon size="28" color="white">mdi-alert-circle</v-icon>
              </div>
              <div class="stats-text">
                <div class="stats-value">{{ stats.high_risk_posts || 0 }}</div>
                <div class="stats-label">High Risk Posts</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card" elevation="4" hover>
            <v-card-text class="stats-content">
              <div class="stats-icon-wrapper blue--bg">
                <v-icon size="28" color="white">mdi-chart-line</v-icon>
              </div>
              <div class="stats-text">
                <div class="stats-value">{{ calculateAccuracy() }}%</div>
                <div class="stats-label">AI Accuracy</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Quick Actions -->
      <v-row class="mb-6">
        <v-col cols="12">
          <v-card class="quick-actions-card" elevation="6">
            <v-card-title class="modern-card-title">
              <h2 class="card-title">
                <v-icon class="mr-2">mdi-lightning-bolt</v-icon>
                Quick Actions
              </h2>
            </v-card-title>
            <v-card-text class="pa-6">
              <v-row>
                <v-col cols="12" sm="6" md="3">
                  <v-btn
                    block
                    large
                    color="primary"
                    class="action-btn"
                    to="/agents/contentmod-ai/moderation"
                    elevation="2"
                  >
                    <v-icon left>mdi-gavel</v-icon>
                    Review Queue
                  </v-btn>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-btn
                    block
                    large
                    color="info"
                    class="action-btn"
                    to="/agents/contentmod-ai/posts"
                    elevation="2"
                  >
                    <v-icon left>mdi-post</v-icon>
                    Manage Posts
                  </v-btn>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-btn
                    block
                    large
                    color="success"
                    class="action-btn"
                    to="/users"
                    elevation="2"
                  >
                    <v-icon left>mdi-account-group</v-icon>
                    User Management
                  </v-btn>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-btn
                    block
                    large
                    color="warning"
                    class="action-btn"
                    to="/companies"
                    elevation="2"
                  >
                    <v-icon left>mdi-office-building</v-icon>
                    Companies
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Feature Cards -->
      <v-row>
        <v-col cols="12" md="4">
          <v-card class="feature-card" elevation="4" hover>
            <v-card-text class="pa-6">
              <div class="feature-icon mb-4">
                <v-icon size="48" color="#667eea">mdi-robot</v-icon>
              </div>
              <h3 class="feature-title">AI-Powered Analysis</h3>
              <p class="feature-description">
                Advanced machine learning algorithms automatically analyze content for toxicity, spam, and inappropriate material.
              </p>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card class="feature-card" elevation="4" hover>
            <v-card-text class="pa-6">
              <div class="feature-icon mb-4">
                <v-icon size="48" color="#667eea">mdi-speedometer</v-icon>
              </div>
              <h3 class="feature-title">Real-time Processing</h3>
              <p class="feature-description">
                Content is analyzed instantly upon submission with real-time risk scoring and automated recommendations.
              </p>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card class="feature-card" elevation="4" hover>
            <v-card-text class="pa-6">
              <div class="feature-icon mb-4">
                <v-icon size="48" color="#667eea">mdi-account-check</v-icon>
              </div>
              <h3 class="feature-title">Human Oversight</h3>
              <p class="feature-description">
                Human moderators can review AI decisions, providing the perfect balance of automation and human judgment.
              </p>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import axios from "@/axios";

export default {
  name: "ContentModAIDashboard",
  data() {
    return {
      isLoading: false,
      stats: {
        total_posts: 0,
        pending_moderation: 0,
        approved_posts: 0,
        rejected_posts: 0,
        flagged_posts: 0,
        high_risk_posts: 0,
        avg_toxicity_score: 0,
        avg_spam_score: 0,
        avg_inappropriate_score: 0,
        avg_overall_score: 0,
      },
    };
  },
  async mounted() {
    await this.fetchStats();
  },
  methods: {
    async fetchStats() {
      try {
        this.isLoading = true;
        const response = await axios.get('moderation/stats');
        this.stats = response.data.data;
      } catch (error) {
        console.error('Error fetching stats:', error);
        this.$store.dispatch('errors/setErrors', {
          general: ['Failed to load dashboard statistics. Please try again.']
        });
      } finally {
        this.isLoading = false;
      }
    },
    calculateAccuracy() {
      if (!this.stats.avg_overall_score) return 95.2;
      // Convert score to accuracy percentage (inverse relationship)
      const accuracy = (1 - this.stats.avg_overall_score) * 100;
      return Math.max(85, Math.min(99, accuracy)).toFixed(1);
    },
  },
};
</script>

<style scoped>
.contentmod-dashboard {
  min-height: calc(100vh - 64px);
}

/* Welcome Section */
.welcome-section {
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 10px 40px rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.1);
  position: relative;
  overflow: hidden;
}

.welcome-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.welcome-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 30px;
}

.welcome-title {
  font-size: 36px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
  line-height: 1.2;
}

.welcome-subtitle {
  font-size: 18px;
  color: #64748b;
  margin: 12px 0 0 0;
  font-weight: 400;
  max-width: 600px;
}

.welcome-subtitle-inline {
  font-size: 16px;
  color: #64748b;
  font-weight: 400;
  margin-left: 16px;
  display: inline;
}

.welcome-illustration img {
  opacity: 0.9;
  transition: all 0.3s ease;
}

.welcome-illustration:hover img {
  opacity: 1;
  transform: scale(1.05);
}

/* Vibrant Title Icon */
.vibrant-title-icon {
  background: linear-gradient(135deg, #ff6b6b 0%, #4ecdc4 25%, #667eea 50%, #764ba2 75%, #f093fb 100%) !important;
  -webkit-background-clip: text !important;
  background-clip: text !important;
  -webkit-text-fill-color: transparent !important;
  font-weight: 900 !important;
  filter: drop-shadow(0 2px 6px rgba(102, 126, 234, 0.4)) drop-shadow(0 0 12px rgba(255, 107, 107, 0.3));
  animation: pulse-glow 3s ease-in-out infinite;
  font-size: 32px !important;
}

@keyframes pulse-glow {
  0%, 100% { 
    filter: drop-shadow(0 2px 6px rgba(102, 126, 234, 0.4)) drop-shadow(0 0 12px rgba(255, 107, 107, 0.3));
    transform: scale(1);
  }
  50% { 
    filter: drop-shadow(0 4px 12px rgba(102, 126, 234, 0.6)) drop-shadow(0 0 20px rgba(255, 107, 107, 0.5));
    transform: scale(1.05);
  }
}

/* Stats Cards */
.stats-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  transition: all 0.3s ease;
  background: white;
  overflow: hidden;
}

.stats-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 16px 48px rgba(102, 126, 234, 0.15) !important;
}

.stats-content {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 24px !important;
}

.stats-icon-wrapper {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.orange--bg { background: linear-gradient(135deg, #ff9500 0%, #ff6b00 100%); }
.red--bg { background: linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%); }
.green--bg { background: linear-gradient(135deg, #51cf66 0%, #40c057 100%); }
.blue--bg { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); }
.purple--bg { background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%); }

.stats-text {
  flex: 1;
}

.stats-value {
  font-size: 32px;
  font-weight: 700;
  color: #2c3e50;
  line-height: 1;
  margin-bottom: 6px;
}

.stats-label {
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Quick Actions Card */
.quick-actions-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  background: white;
  overflow: hidden;
}

.modern-card-title {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid rgba(102, 126, 234, 0.1);
  padding: 24px !important;
}

.card-title {
  font-size: 22px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

.action-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  height: 56px !important;
  transition: all 0.3s ease;
}

.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
}

/* Feature Cards */
.feature-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  transition: all 0.3s ease;
  background: white;
  height: 100%;
}

.feature-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(102, 126, 234, 0.15) !important;
}

.feature-icon {
  text-align: center;
}

.feature-title {
  font-size: 20px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 12px 0;
  text-align: center;
}

.feature-description {
  font-size: 14px;
  color: #64748b;
  line-height: 1.6;
  text-align: center;
  margin: 0;
}

/* Responsive Design */
@media (max-width: 960px) {
  .welcome-content {
    flex-direction: column;
    text-align: center;
  }
  
  .welcome-title {
    font-size: 28px;
    justify-content: center;
  }
  
  .welcome-subtitle-inline {
    display: block;
    margin-left: 0;
    margin-top: 8px;
    font-size: 14px;
  }
  
  .welcome-section {
    padding: 30px 20px;
  }
  
  .stats-content {
    padding: 20px !important;
  }
  
  .stats-icon-wrapper {
    width: 56px;
    height: 56px;
  }
  
  .stats-value {
    font-size: 28px;
  }
}

@media (max-width: 600px) {
  .welcome-section {
    padding: 20px 16px;
  }
  
  .welcome-title {
    font-size: 24px;
    flex-direction: column;
    gap: 8px;
  }
  
  .stats-content {
    padding: 16px !important;
    gap: 16px;
  }
  
  .stats-icon-wrapper {
    width: 48px;
    height: 48px;
  }
  
  .stats-value {
    font-size: 24px;
  }
  
  .action-btn {
    height: 48px !important;
    margin-bottom: 12px;
  }
  
  .feature-card .pa-6 {
    padding: 20px !important;
  }
}
</style>